// ROI case-study math (marketing). Pure + unit-tested so the table, the
// break-even, and the kicker always agree with each other for any team size.
// Prices are the sourced list rates; everything else is derived.

export const ROI = {
  /** Slack Pro list price, per user / month. */
  slackProPerUserMonth: 7.25,
  /** Slack Business+ list price, per user / month. */
  slackBizPerUserMonth: 12.5,
  /** Discord Level-2 boost, flat / month (≈7 boosts). */
  boostMonthly: 35,
  /** One-time Slackcord migration (illustrative, the case-study workspace). */
  migrationOneTime: 1729,
  /** Extra part-time helpers used by the kicker. */
  helperCount: 5,
  years: 5,
} as const;

export interface RoiResult {
  users: number;
  proMonthly: number;
  proYear: number;
  bizMonthly: number;
  bizYear: number;
  boostYear: number;
  /** 5-year totals. */
  slackcordTotal: number; // one-time + boost × 60mo
  slackBizTotal: number; // biz × 60mo
  savings: number; // slack − slackcord over the period
  /** Months for the one-time migration to pay for itself vs Business+, or null. */
  breakEvenMonths: number | null;
  breakEvenYears: number | null;
  /** The kicker: same team + N seasonal helpers. */
  helperUsers: number;
  bizYearWithHelpers: number;
  perHelperYear: number; // what one extra seat costs on Slack Business+, per year
}

/** Compute the full ROI picture for a team of `users` on Slack Business+. */
export function computeRoi(users: number): RoiResult {
  const n = Math.max(1, Math.round(users));
  const months = ROI.years * 12;

  const proMonthly = n * ROI.slackProPerUserMonth;
  const bizMonthly = n * ROI.slackBizPerUserMonth;
  const boostYear = ROI.boostMonthly * 12;

  const slackcordTotal = ROI.migrationOneTime + ROI.boostMonthly * months;
  const slackBizTotal = bizMonthly * months;

  // Break-even: one-time migration ÷ monthly saving vs Business+ (flat boost).
  const monthlySaving = bizMonthly - ROI.boostMonthly;
  const breakEvenMonths =
    monthlySaving > 0 ? Math.round(ROI.migrationOneTime / monthlySaving) : null;

  const helperUsers = n + ROI.helperCount;

  return {
    users: n,
    proMonthly,
    proYear: proMonthly * 12,
    bizMonthly,
    bizYear: bizMonthly * 12,
    boostYear,
    slackcordTotal,
    slackBizTotal,
    savings: slackBizTotal - slackcordTotal,
    breakEvenMonths,
    breakEvenYears: breakEvenMonths === null ? null : breakEvenMonths / 12,
    helperUsers,
    bizYearWithHelpers: helperUsers * ROI.slackBizPerUserMonth * 12,
    perHelperYear: ROI.slackBizPerUserMonth * 12,
  };
}

/** Whole-dollar USD, e.g. 1500 → "$1,500". */
export function usd0(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}
